// Shared, frontend-only helpers for associating a saved Thumbnail design
// with its source video/clip, and for picking a "default" per platform —
// used by Images & Posts, and imported as-is by the Optimize workspace
// (`OptimizeWorkspace.jsx`) for its linked-thumbnail resolution. Schedule
// does not import this yet (Level 3, see notes below). Everything here
// reads/writes the SAME localStorage records Images & Posts already
// writes (`consistency_images_posts_{projectId}` for designs,
// `consistency_thumbnail_defaults_{projectId}` for the default map) —
// there is no backend "thumbnail design" model yet, so none of this is
// server-persisted or visible outside this browser.

const designsKeyFor = (projectId) => `consistency_images_posts_${projectId}`;
const defaultsKeyFor = (projectId) => `consistency_thumbnail_defaults_${projectId}`;

function readJson(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); } catch { return fallback; }
}

export function sourceKeyFor(sourceKind, sourceId) {
  return `${sourceKind}:${sourceId}`;
}

/** All thumbnail-asset-type designs saved for a project. */
export function getThumbnailDesigns(projectId) {
  if (!projectId) return [];
  return readJson(designsKeyFor(projectId), []).filter((d) => d.assetType === 'thumbnail');
}

/** Thumbnail candidates for one specific video or clip (section 6). */
export function getThumbnailCandidatesForSource(projectId, sourceKind, sourceId) {
  return getThumbnailDesigns(projectId).filter((d) => d.sourceRef?.kind === sourceKind && d.sourceRef?.id === sourceId);
}

/** The full default map: { "kind:id": { [platform]: { designId, variantId } } }. */
export function getDefaultThumbnailMap(projectId) {
  if (!projectId) return {};
  return readJson(defaultsKeyFor(projectId), {});
}

export function setDefaultThumbnail(projectId, sourceKind, sourceId, platform, designId, variantId) {
  if (!projectId) return;
  const map = getDefaultThumbnailMap(projectId);
  const key = sourceKeyFor(sourceKind, sourceId);
  map[key] = { ...(map[key] || {}), [platform]: { designId, variantId } };
  localStorage.setItem(defaultsKeyFor(projectId), JSON.stringify(map));
}

/**
 * Resolves the default thumbnail image URL for a source/platform, falling
 * back to the first candidate if no explicit default was ever set. Used
 * by `OptimizeWorkspace.jsx`'s thumbnail priority resolution (Schedule
 * does not call this yet — Level 3).
 */
export function getDefaultThumbnail(projectId, sourceKind, sourceId, platform) {
  const candidates = getThumbnailCandidatesForSource(projectId, sourceKind, sourceId);
  if (candidates.length === 0) return null;
  const map = getDefaultThumbnailMap(projectId);
  const entry = map[sourceKeyFor(sourceKind, sourceId)]?.[platform];
  const design = (entry && candidates.find((d) => d.id === entry.designId)) || candidates[0];
  const variant = (entry && design.variants.find((v) => v.id === entry.variantId))
    || design.variants.find((v) => v.platform === platform)
    || design.variants[0];
  return { design, variant, imageUrl: resolveThumbnailImageUrl(design, variant), isExplicitDefault: !!entry };
}

/** Best-effort preview image for a thumbnail design/variant — the frame-capture element if present, else the first image element. */
export function resolveThumbnailImageUrl(design, variant) {
  const page = variant?.pages?.[0] || design?.variants?.[0]?.pages?.[0];
  if (!page) return null;
  const frameEl = page.elements.find((e) => e.isFrameCapture) || page.elements.find((e) => e.type === 'image');
  return frameEl?.src || null;
}
